import { IScreen } from './IScreen';
import { ScreenStatus } from './ScreenStatus';
import { RegieState, RosterPlayer } from './regie';

/** Presence window (kept in sync with the admin live dashboard's SELF_WINDOW_MS). */
export const PRESENCE_WINDOW_MS = 4 * 60 * 1000;

/** `lastSeen` this much newer than every new-bundle mark = written by an old-bundle TV. */
const LEGACY_GAP_MS = 5_000;

const freshWithin = (windowMs: number) => (t?: number) => t !== undefined && Date.now() - t < windowMs;

export class Screen implements IScreen {
    /* The screen identifier */
    _id: string = '';

    /* The status of the screen */
    _status: ScreenStatus = ScreenStatus.Off;

    /* The screen name */
    _name?: string;

    /**
     * A brief description of the screen.
     * It can consists of its location, how to recognize it, etc.
     */
    _description?: string;

    /**
     * A reference to the current screen.
     */
    _currentSessionID?: string;

    /**
     * A reference to the game identifier.
     */
    _gameID?: string;

    /**
     * A reference to the game identifier.
     */
    _instanceID?: string;

    /* Live count of connected gamepads (additive field, debounced write). */
    _playerCount: number = 0;

    /* Presence heartbeat in epoch millis (additive field, server timestamp). */
    _lastSeen?: number;

    /* Animator Control: presence heartbeat of the piloting animator app, epoch
       millis. Fresh = an animator is driving this screen from their phone. */
    _animatorSeen?: number;

    /* Presence beats of the TV page coming from a phone / from a real TV (epoch
       millis, additive). Old screens write neither. */
    _phoneSeen?: number;

    _tvSeen?: number;

    /* When a (new-bundle) TV page went to the background (hidden tab, app switched,
       window minimised): it stopped beating and cleared its own *Seen field. */
    _hiddenAt?: number;

    /* Animator Control: when true, the screen shows the big players-QR overlay. */
    _showQR: boolean = false;

    /* Animator Control remote commands (V1.1) — applied live by the screen. */
    _paused: boolean = false;

    _muted: boolean = false;

    _idle: boolean = false;

    /* Animator Control (Phase 2A): one-shot "launch the game" signal. Epoch-ms
       nonce bumped by the cockpit each time the animator presses « Lancer » — the
       screen relays it as an onMessage('launch') into the game iframe. Additive,
       never read on the gameplay path. */
    _launchRequest?: number;

    /* Double écran : nonce de « Changer de jeu » (changeGame), pour que l'écran public
       ne confonde pas un changement de jeu avec « Reprendre ». Additive. */
    _gameChangedAt?: number;

    /* Animator Control (Phase 2B): current-game parameters set remotely by the
       animator (e.g. Blind Test playlist / number of songs / proposal mode). Opaque
       blob relayed to the game ('set-game-params') which applies what it understands.
       Additive, never read on the gameplay path. */
    _gameParams?: Record<string, unknown>;

    /* Réglages mémorisés PAR JEU (gameID → réglages), écrits par la télécommande ET
       par la TV (jeux de `MEMORIZED_GAMES`, cf config/gameParamsMemory.ts). Survivent
       au changement de jeu, contrairement à `gameParams`. Additive. */
    _gameParamsByGame?: Record<string, Record<string, unknown>>;

    /* Animator Control (Phase 2D): one-shot "stop the game / back to lobby" signal.
       Epoch-ms nonce bumped when the animator presses « Stopper » — relayed as
       onMessage('remote-stop') so the game runs its own back-to-lobby. Additive. */
    _stopRequest?: number;

    /* Animator Control: generic in-game command {name, nonce} from the cockpit
       (e.g. Unquiz « show-next »). Relayed as onMessage(name) — games run the
       commands they understand and ignore the rest. Additive. */
    _gameCommand?: { name: string; nonce: number };

    /* Languages cycled on the free-play attract screen for THIS screen (e.g.
       ['zh','en'] on an Asia/Africa transfer gate). The language INSIDE the games
       is already picked by each passenger on their phone — this only localises the
       TV lobby, i.e. what a traveller reads before deciding to scan. Additive:
       undefined (every legacy screen) → the FR/EN default, so no screen changes
       behaviour until the field is set. */
    _idleLanguages?: string[];

    /* Double écran « Régie » (formule Pro) : ce que l'écran public affiche et les
       textes de la soirée, écrits par la régie (cf types/screen/regie.ts). Additive. */
    _regie?: RegieState;

    /* Joueurs connectés (pseudo, avatar), écrits par l'écran pour la régie. Additive. */
    _roster?: RosterPlayer[];

    get id(): string {
        return this._id;
    }

    get status(): ScreenStatus {
        return this._status;
    }

    get gameID(): string | undefined {
        return this._gameID;
    }

    get instanceID(): string | undefined {
        return this._instanceID;
    }

    get name(): string {
        return this._name || this._id;
    }

    get description(): string {
        return this._description || 'There is no description for this game.';
    }

    get playerCount(): number {
        return this._playerCount;
    }

    get lastSeen(): number | undefined {
        return this._lastSeen;
    }

    get showQR(): boolean {
        return this._showQR;
    }

    get paused(): boolean {
        return this._paused;
    }

    get muted(): boolean {
        return this._muted;
    }

    get idle(): boolean {
        return this._idle;
    }

    get animatorSeen(): number | undefined {
        return this._animatorSeen;
    }

    /**
     * Last beat written by a TV page on the OLD bundle (it writes `lastSeen` only):
     * a `lastSeen` clearly newer than every new-bundle mark (`tvSeen`, `phoneSeen`,
     * `hiddenAt`). New-bundle beats write `lastSeen` together with their own field,
     * so they never qualify. A screen that never ran the new bundle → `lastSeen`.
     * Only the LATEST write is visible here: callers that watch the screen over time
     * should remember it (useLegacyBeat), since a newer phone beat masks it.
     */
    legacyBeat(): number | undefined {
        if (this._lastSeen === undefined) return undefined;
        const newest = Math.max(this._tvSeen ?? -Infinity, this._phoneSeen ?? -Infinity, this._hiddenAt ?? -Infinity);
        return this._lastSeen - newest > LEGACY_GAP_MS ? this._lastSeen : undefined;
    }

    private __legacy(legacyBeatAt?: number) {
        return Math.max(this.legacyBeat() ?? -Infinity, legacyBeatAt ?? -Infinity);
    }

    get launchRequest(): number | undefined {
        return this._launchRequest;
    }

    get gameChangedAt(): number | undefined {
        return this._gameChangedAt;
    }

    get gameParams(): Record<string, unknown> | undefined {
        return this._gameParams;
    }

    get gameParamsByGame(): Record<string, Record<string, unknown>> | undefined {
        return this._gameParamsByGame;
    }

    get stopRequest(): number | undefined {
        return this._stopRequest;
    }

    get gameCommand(): { name: string; nonce: number } | undefined {
        return this._gameCommand;
    }

    get idleLanguages(): string[] | undefined {
        return this._idleLanguages;
    }

    get regie(): RegieState | undefined {
        return this._regie;
    }

    get roster(): RosterPlayer[] {
        return this._roster ?? [];
    }

    isAvailable(): boolean {
        return this.status === ScreenStatus.On;
    }

    /**
     * Presence check for the animator control: an anim/bar screen is "live" only
     * if it sent a heartbeat recently. We do NOT fall back to `status` because
     * `stopScreenSession` is never called on a normal stop (see collector) → the
     * status stays `on` forever, which would mark every past establishment live.
     *
     * Default window kept in sync with the admin live dashboard's SELF_WINDOW_MS
     * (`unboared-b2b/src/hooks/admin/useLiveSessions.ts` = 4 min) so an
     * establishment never reads LIVE on one surface and offline on the other.
     * Heartbeat cadence is 60s (ScreenHeartbeat) → tolerates ~3 missed beats.
     *
     * Visible pages only (new bundle): a TV page sent to the background writes
     * `hiddenAt` and clears its `tvSeen` / `phoneSeen`, so it stops counting at once
     * instead of reading LIVE for 4 more minutes (Le Cuba, 20/10/2026: frozen phone
     * tab, « En direct » on the remote, Launch went nowhere). TVs still on the old
     * bundle only write `lastSeen` → `legacyBeat()` keeps them live as before.
     */
    isLive(windowMs: number = PRESENCE_WINDOW_MS, legacyBeatAt?: number): boolean {
        const fresh = freshWithin(windowMs);
        return fresh(this._tvSeen) || fresh(this._phoneSeen) || fresh(this.__legacy(legacyBeatAt));
    }
}
